/*
 * The corrugation card's layers (085): the board as it is built.
 *
 * Layer 1 is the printed face -- the board on the card, or kraft when the job
 * is printed on kraft -- then flutes and liners alternate under it, even
 * layers fluting and odd ones lining. The same rule the costing sheet uses.
 */

/**
 * Corrugated board starts and ends with a liner, so the count is odd. A mono
 * carton (1 ply) has no corrugation card at all: its route leaves Corrugation and
 * Flute Pasting out (Flute Pasting shows the card too, for board bought corrugated).
 */
export const PLY_CHOICES = [3, 5, 7] as const;

/** Said wherever a card still carries 1 ply from before. */
export const ONE_PLY = '1 ply has nothing to corrugate. Choose 3, 5 or 7, or take Corrugation and Flute Pasting off the route.';

export const plyName = (ply: number) => (ply === 1 ? '1 ply · mono carton' : `${ply} ply`);

export type LayerRole = 'BOARD' | 'KRAFT_FACE' | 'FLUTE' | 'LINER';

export function roleOf(layer: number, kraftPrinting: boolean): LayerRole {
  if (layer === 1) return kraftPrinting ? 'KRAFT_FACE' : 'BOARD';
  return layer % 2 === 0 ? 'FLUTE' : 'LINER';
}

export const ROLE_LABEL: Record<LayerRole, string> = {
  BOARD: 'Board', KRAFT_FACE: 'Kraft face', FLUTE: 'Flute', LINER: 'Liner',
};

export type Paper = 'KRAFT' | 'WHITE_BACK';
export const PAPER_LABEL: Record<Paper, string> = { KRAFT: 'Kraft', WHITE_BACK: 'White back' };

/** Flute profiles, tallest to finest. */
export const FLUTES = ['A', 'B', 'C', 'E', 'F', 'N'] as const;

/** "E Flute" on the box type is the flute the card starts from. */
export const fluteOfBoxType = (boxType: string | null | undefined) =>
  (boxType ?? '').match(/\b([ABCEFN])\s*-?\s*flute\b/i)?.[1]?.toUpperCase() ?? '';

/** One layer as the form holds it. */
export type LayerDraft = {
  layer: number; material: Paper; flute: string;
  linerSize: string; linerQty: string; gsm: string; bf: string; notes: string;
};

/** A layer as the card sends it back. */
export interface LayerRow {
  sequenceNo?: number; material?: Paper | null; flute?: string | null;
  linerSize: string | null; linerQty: string | number | null;
  gsm: string | number | null; bf: string | number | null; notes?: string | null;
}

const s = (v: unknown) => (v === null || v === undefined ? '' : String(v));
const n = (v: unknown) => (v === null || v === undefined || v === '' ? '' : String(Number(v)));

/** The card's rows as drafts, each keeping its place in the board. */
export function draftsOf(rows: LayerRow[]): LayerDraft[] {
  return rows.map((l, i) => ({
    layer: l.sequenceNo ?? i + 2,
    material: l.material ?? 'KRAFT',
    flute: s(l.flute),
    linerSize: s(l.linerSize), linerQty: n(l.linerQty), gsm: n(l.gsm), bf: n(l.bf), notes: s(l.notes),
  }));
}

/** The layers a card of this ply describes: 2 and down, and 1 when kraft is printed. */
export function layerNumbers(ply: number | null, kraftPrinting: boolean): number[] {
  if (!ply) return [];
  const out: number[] = [];
  for (let k = kraftPrinting ? 1 : 2; k <= ply; k += 1) out.push(k);
  return out;
}

/**
 * The rows for a ply: what was typed stays at its layer, a new layer starts
 * at the running size and the sheets (one layer per sheet), kraft, with the
 * box type's flute on a flute.
 */
export function layersFor(
  ply: number | null, kraftPrinting: boolean, have: LayerDraft[],
  start: { size: string; qty: string; flute: string },
): LayerDraft[] {
  return layerNumbers(ply, kraftPrinting).map((k) => have.find((l) => l.layer === k) ?? {
    layer: k, material: 'KRAFT', flute: roleOf(k, kraftPrinting) === 'FLUTE' ? start.flute : '',
    linerSize: start.size, linerQty: start.qty, gsm: '', bf: '', notes: '',
  });
}

/** Every layer has its GSM and BF -- what makes the corrugation card complete. */
export const layersComplete = (ply: number | null, kraftPrinting: boolean, rows: LayerDraft[]) =>
  ply !== null && ply >= 3 && layerNumbers(ply, kraftPrinting).every((k) => {
    const r = rows.find((l) => l.layer === k);
    return Boolean(r?.gsm.trim() && r.bf.trim());
  });

/** What the server stores: only the layers this ply has, each saying what it is. */
export function layersBody(ply: number | null, kraftPrinting: boolean, rows: LayerDraft[]) {
  const keep = new Set(layerNumbers(ply, kraftPrinting));
  return rows.filter((r) => keep.has(r.layer)).map((r) => {
    const role = roleOf(r.layer, kraftPrinting);
    return {
      layer: r.layer,
      material: role === 'LINER' ? r.material : null,
      flute: role === 'FLUTE' && r.flute.trim() ? r.flute.trim() : null,
      ...(r.linerSize.trim() ? { linerSize: r.linerSize.trim() } : {}),
      ...(r.linerQty ? { linerQty: Number(r.linerQty) } : {}),
      ...(r.gsm ? { gsm: Number(r.gsm) } : {}),
      ...(r.bf ? { bf: Number(r.bf) } : {}),
      ...(r.notes.trim() ? { notes: r.notes.trim() } : {}),
    };
  });
}

/** "Flute · E", "Liner · White back", "Board": what a layer is, in a word or two. */
export function layerName(layer: number, kraftPrinting: boolean, r?: { material?: Paper | null; flute?: string | null }) {
  const role = roleOf(layer, kraftPrinting);
  if (role === 'FLUTE') return r?.flute ? `Flute · ${r.flute}` : 'Flute';
  if (role === 'LINER') return `Liner · ${PAPER_LABEL[r?.material ?? 'KRAFT']}`;
  return ROLE_LABEL[role];
}
